import { DownloadOutlined } from "@ant-design/icons";
import { Col, Radio, Row } from "antd";
import TextArea from "antd/es/input/TextArea";
import to from "await-to-js";
import dayjs from "dayjs";
import { t } from "i18next";
import React, { useContext, useMemo } from "react";

import { DatePicker } from "../../../../../../../../components/DatePicker/DatePicker";
import FileUploader from "../../../../../../../../components/FileUploader/FileUploader";
import { Input } from "../../../../../../../../components/Input/Input";
import { ModalsContext } from "../../../../../../../../context/modals.context";
import { DATE_FORMAT } from "../../../../../../../../core/constants";
import { MinistryContext } from "../../../../../../../../modules/ministry/ministry.context";
import { ministryService } from "../../../../../../../../modules/ministry/ministry.service";
import { IMfinKomentariProps } from "../../../../../../../../modules/ministry/ministry.types";
import { DocumentApi } from "../../../../../../../../types";
import { formatDateDDMMYY } from "../../../../../../../../utils/dateFormatter";
import { downloadFile } from "../../../../../../../../utils/fileDownload";
import { toastService } from "../../../../../../../../utils/toast.service";
import style from "./Ministry.module.scss";

interface IMinistryProps {
  licence?: boolean;
  data: IMfinKomentariProps | null;
  isDisabled: boolean;
}

export const Ministry: React.FC<IMinistryProps> = ({
  licence,
  data,
  isDisabled,
}) => {
  const { ministry, setMinistry } = useContext(MinistryContext);
  const { setError } = useContext(ModalsContext);

  const handleUpload = (obj: DocumentApi) => {
    setMinistry({ dokumentResenja: obj });
  };

  const createDohvatiDokumentMfin = async () => {
    const [err, res] = await to(
      ministryService.createDohvatiDokumentMfin({
        documentId: data?.dokumentResenja.id as any,
      })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    toastService.success("TOAST.DOWNLOAD_SUCCESS");

    downloadFile(
      res.data.data,
      data?.dokumentResenja?.dokumentUrl
        ? data?.dokumentResenja?.dokumentUrl
        : "dokument",
      res.data.format as any
    );
  };

  const handleRemove = () => {
    setMinistry({ dokumentResenja: undefined });
  };

  const isPoreskaPositiveDisabled = useMemo(() => {
    return typeof data?.isPoreskaPositive === "boolean";
  }, [data?.isPoreskaPositive]);

  const isPoreskaPositiveValue = useMemo(() => {
    if (
      typeof data?.isPoreskaPositive !== "boolean" &&
      typeof ministry.isPoreskaPositive !== "boolean"
    ) {
      return undefined;
    }

    return Boolean(data?.isPoreskaPositive ?? ministry.isPoreskaPositive);
  }, [data?.isPoreskaPositive, ministry?.isPoreskaPositive]);

  return (
    <Col className={style.mainContainer}>
      <Col span={24}>
        <Col className="pb-40 textBold" span={24}>
          <span className={style.fontSize}>
            {t("AKCIZNE_DOZVOLE.MINISTARSTVO")}
          </span>
        </Col>
        {licence ? (
          <Radio.Group
            className="mb-20"
            value={isPoreskaPositiveValue}
            onChange={(event) =>
              setMinistry({ isPoreskaPositive: event.target.value })
            }
            disabled={isPoreskaPositiveDisabled || isDisabled}
          >
            <Radio className={style.fontSize} value={true}>
              {t("COMMON.POZITIVNO")}
            </Radio>
            <Radio className={style.fontSize} value={false}>
              {t("COMMON.NEGATIVNO")}
            </Radio>
          </Radio.Group>
        ) : null}
        <Row className="spaceBetween mb-25">
          <Col span={6}>
            {data && (
              <div className={style.label}>{t("AKCIZNE_DOZVOLE.KOMENTAR")}</div>
            )}
            <TextArea
              allowClear
              disabled={!!data?.komentar || isDisabled}
              value={data?.komentar || ministry.komentar}
              className={style.fontSize}
              onChange={(event) =>
                setMinistry({ komentar: event?.target.value })
              }
              autoSize={{ minRows: 3 }}
              placeholder={t("AKCIZNE_DOZVOLE.KOMENTAR")}
              name="mfin-komentar-poreska-skladiste"
            />
          </Col>
          <Col span={6}>
            {data?.dokumentResenja ? (
              <>
                <div className={style.label}>
                  {t("AKCIZNE_DOZVOLE.POTPISANO_RESENJE")}
                </div>
                <div
                  className={style.documentTitle}
                  onClick={() => createDohvatiDokumentMfin()}
                >
                  {data?.dokumentResenja.dokumentUrl ? (
                    <DownloadOutlined />
                  ) : null}{" "}
                  {data?.dokumentResenja.dokumentUrl || ""}
                </div>
              </>
            ) : (
              <FileUploader
                onUpload={handleUpload}
                onRemove={handleRemove}
                uploadButtonLabel={t("AKCIZNE_MARKICE.DODAJ")}
              />
            )}
          </Col>
        </Row>
        <Col className="pb-25" span={6}>
          {data && (
            <div className={style.label}>{t("REGISTRI.BROJ_RESENJA")}</div>
          )}
          <Input
            allowClear
            disabled={!!data?.brojResenja || isDisabled}
            value={data?.brojResenja || ministry.brojResenja}
            className={style.fontSize}
            onChange={(event) =>
              setMinistry({ brojResenja: event?.target.value })
            }
            name="broj-resenja-ministarstvo"
            placeholder={t("PLACEHOLDERS.BROJ_RESENJA")}
          />
        </Col>
        <Col span={6}>
          {data ? (
            <>
              <div className={style.label}>
                {t("TABLES.DATUM_OD_KADA_VAZI_RESENJE")}
              </div>
              <Input
                name="datum"
                disabled
                value={formatDateDDMMYY(data.datumVazenja) as any}
              />
            </>
          ) : (
            <DatePicker
              allowClear
              defaultValue={dayjs().add(1, "day")}
              className={style.fontSize}
              showToday={false}
              onChange={(value) => setMinistry({ datumVazenja: value })}
              format={DATE_FORMAT}
              value={
                ministry.datumVazenja ? dayjs(ministry.datumVazenja) : null
              }
              placeholder={t("TABLES.DATUM_OD_KADA_VAZI_RESENJE")}
              name="datum-vazenja-ministarstvo"
              disabled={isDisabled}
            />
          )}
        </Col>
      </Col>
    </Col>
  );
};
